import { computed, onMounted, onUnmounted, ref, type Ref, type ComputedRef } from 'vue'
import {
  postStockAsync, postInvoicesUnassociated, postPaymentsUnassociated,
  retryTask as lytAccountingRetryTask,
  type TaskSummaryDto,
} from '@/api/lytAccountingClient'
import { useTaskActivityStore } from '@/stores/taskActivityStore'

export type BulkEntity = 'items' | 'invoices' | 'payments'

export interface InFlightEntityRow {
  entityType: string
  count: number
}

export interface UseDashboardSyncActions {
  // State refs
  bulkDropdownOpen: Ref<boolean>
  bulkSyncing: Ref<boolean>
  bulkFeedback: Ref<string>
  retryingTaskId: Ref<number | string | null>
  retryFeedback: Ref<string>
  error: Ref<string>
  lastRefreshAt: Ref<Date | null>
  // Computeds
  failedInLast24h: ComputedRef<number>
  failedTotal: ComputedRef<number>
  inFlightByEntityType: ComputedRef<InFlightEntityRow[]>
  // Handlers
  bulkSyncEntity: (entity: BulkEntity) => Promise<void>
  retryTask: (taskId: number | string) => Promise<void>
  closeDropdown: () => void
}

const IN_FLIGHT_ENTITY_ORDER: ReadonlyArray<string> = ['CUSTOMER', 'INVOICE', 'ITEM', 'PAYMENT', 'STOCK']
const REFRESH_PARAMS = {
  includeSummary: true,
  includeRecent: true,
  includeActive: true,
  entityTypes: ['STOCK', 'ITEM', 'INVOICE', 'PAYMENT'] as string[],
  recentLimit: 50,
}

/**
 * useDashboardSyncActions: Path B composable for the DashboardView
 * split-button + per-row retry affordance. Mirrors the useInvoicesPage
 * shape. The dashboard page owns the taskActivity subscription; this
 * composable only reads the populated store refs.
 */
export function useDashboardSyncActions(): UseDashboardSyncActions {
  const taskActivity = useTaskActivityStore()

  const bulkDropdownOpen = ref(false)
  const bulkSyncing = ref(false)
  const bulkFeedback = ref('')
  const retryingTaskId = ref<number | string | null>(null)
  const retryFeedback = ref('')
  const error = ref('')
  const lastRefreshAt = ref<Date | null>(null)

  const failedInLast24h = computed<number>(() => {
    const cutoff = Date.now() - 24*60*60*1000
    return taskActivity.recentTasks.filter((task: TaskSummaryDto) => {
      if (task.status !== 'failed' && task.status !== 'dead_lettered') return false
      return Date.parse(task.updatedAt) > cutoff
    }).length
  })

  const failedTotal = computed<number>(() => taskActivity.summaries.reduce(
    (acc: number, summary) => acc + summary.failedCount + summary.deadLetteredCount,
    0,
  ))

  const inFlightByEntityType = computed<InFlightEntityRow[]>(() => {
    const rows: InFlightEntityRow[] = []
    for (const entityType of IN_FLIGHT_ENTITY_ORDER) {
      const count = taskActivity.activeTasksByEntityType[entityType]?.length ?? 0
      if (count > 0) rows.push({ entityType, count })
    }
    return rows
  })

  function closeDropdown(): void {
    bulkDropdownOpen.value = false
  }

  function handleOutsideClick(event: MouseEvent): void {
    if (!bulkDropdownOpen.value) return
    if (!(event.target instanceof Element)) return
    if (!event.target.closest('.split-button')) bulkDropdownOpen.value = false
  }

  function handleEscapeKey(event: KeyboardEvent): void {
    if (event.key === 'Escape') bulkDropdownOpen.value = false
  }

  async function bulkSyncEntity(entity: BulkEntity): Promise<void> {
    bulkSyncing.value = true
    error.value = ''
    try {
      if (entity === 'items') {
        await postStockAsync({ scope: 'full-sync' })
        bulkFeedback.value = 'Bulk sync started (items, full-sync)'
      } else {
        const res = entity === 'invoices'
          ? await postInvoicesUnassociated()
          : await postPaymentsUnassociated()
        bulkFeedback.value = res.taskId
          ? `Bulk sync started (${res.totalUnassociated} ${entity} in batches, task ${res.taskId})`
          : `No unassociated ${entity} to sync`
      }
      await taskActivity.refreshNow(REFRESH_PARAMS)
      lastRefreshAt.value = new Date()
    } catch (e) {
      console.error(`Dashboard bulk sync (${entity}) failed:`, e)
      error.value = (e as Error)?.message || `Bulk ${entity} sync failed`
    } finally {
      bulkSyncing.value = false
    }
  }

  async function retryTaskFn(taskId: number | string): Promise<void> {
    if (retryingTaskId.value !== null) return
    retryingTaskId.value = taskId
    error.value = ''
    try {
      const res = await lytAccountingRetryTask(taskId)
      retryFeedback.value = `Retry queued for task ${res.taskId}`
      await taskActivity.refreshNow(REFRESH_PARAMS)
      lastRefreshAt.value = new Date()
    } catch (e) {
      console.error(`Dashboard retry (${taskId}) failed:`, e)
      error.value = (e as Error)?.message || 'Retry failed'
    } finally {
      retryingTaskId.value = null
    }
  }

  onMounted(() => {
    document.addEventListener('click', handleOutsideClick)
    document.addEventListener('keydown', handleEscapeKey)
  })

  onUnmounted(() => {
    document.removeEventListener('click', handleOutsideClick)
    document.removeEventListener('keydown', handleEscapeKey)
  })

  return {
    bulkDropdownOpen,
    bulkSyncing,
    bulkFeedback,
    retryingTaskId,
    retryFeedback,
    error,
    lastRefreshAt,
    failedInLast24h,
    failedTotal,
    inFlightByEntityType,
    bulkSyncEntity,
    retryTask: retryTaskFn,
    closeDropdown,
  }
}
